import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { Button } from '@/components/ui/Button';
import { useCreatePromoCode } from '@/hooks/usePromoCodes';
import type { CreatePromoCodeInput } from '@/types/promoCode.types';

const planOptions = [
  { value: 'pro', label: 'Pro' },
  { value: 'basic', label: 'Basic' },
];

const durationOptions = [
  { value: '1', label: '1 mes' },
  { value: '3', label: '3 meses' },
  { value: '6', label: '6 meses' },
  { value: '12', label: '12 meses' },
  { value: '0', label: 'Vitalicio' },
];

interface CreatePromoCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreatePromoCodeModal({
  isOpen,
  onClose,
}: CreatePromoCodeModalProps) {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [plan, setPlan] = useState('pro');
  const [durationMonths, setDurationMonths] = useState('1');
  const [maxUses, setMaxUses] = useState('');
  const [unlimitedUses, setUnlimitedUses] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [noExpiry, setNoExpiry] = useState(true);
  const [error, setError] = useState('');

  // Success state
  const [createdInviteLink, setCreatedInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useCreatePromoCode();

  const resetForm = () => {
    setCode('');
    setDescription('');
    setPlan('pro');
    setDurationMonths('1');
    setMaxUses('');
    setUnlimitedUses(true);
    setExpiresAt('');
    setNoExpiry(true);
    setError('');
    setCreatedInviteLink(null);
    setCopied(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!code || code.length < 4 || code.length > 30) {
      setError('Codigo deve ter entre 4 e 30 caracteres');
      return;
    }

    if (!/^[A-Z0-9]+$/.test(code)) {
      setError('Codigo deve conter apenas letras e numeros');
      return;
    }

    const input: CreatePromoCodeInput = {
      code: code.toUpperCase(),
      description: description || undefined,
      plan: plan as 'basic' | 'pro',
      durationMonths: parseInt(durationMonths),
      maxUses: unlimitedUses ? null : parseInt(maxUses) || null,
      expiresAt: noExpiry ? null : expiresAt ? new Date(expiresAt).toISOString() : null,
    };

    createMutation.mutate(input, {
      onSuccess: (data) => {
        setCreatedInviteLink(data.inviteLink);
      },
      onError: (err: any) => {
        const message = err.response?.data?.error || err.message || 'Erro ao criar codigo';
        if (message.toLowerCase().includes('already exists')) {
          setError('Este codigo ja existe');
        } else {
          setError(message);
        }
      },
    });
  };

  const handleCopyLink = async () => {
    if (!createdInviteLink) return;
    try {
      await navigator.clipboard.writeText(createdInviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Success view
  if (createdInviteLink) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Codigo Criado!"
        size="md"
      >
        <div className="space-y-4">
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
              Link de Convite
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm bg-white dark:bg-gray-800 p-2 rounded border border-green-200 dark:border-green-700 break-all">
                {createdInviteLink}
              </code>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCopyLink}
                leftIcon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              >
                {copied ? 'Copiado!' : 'Copiar'}
              </Button>
            </div>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
            Compartilhe este link com os usuarios para ativarem o codigo.
          </p>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={handleClose}>
              Fechar
            </Button>
            <Button
              onClick={() => {
                resetForm();
              }}
            >
              Criar Outro
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Criar Codigo Promocional"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Code */}
        <Input
          label="Codigo *"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="BETA2025"
          hint="Apenas letras e numeros, 4-30 caracteres"
          error={error && error.includes('codigo') ? error : undefined}
        />

        {/* Description */}
        <Textarea
          label="Descricao"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Beta testers - acesso antecipado"
          className="min-h-[80px]"
        />

        {/* Plan and Duration */}
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Plano"
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            options={planOptions}
          />
          <Select
            label="Duracao"
            value={durationMonths}
            onChange={(e) => setDurationMonths(e.target.value)}
            options={durationOptions}
          />
        </div>

        {/* Max Uses */}
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                label="Limite de usos"
                type="number"
                min="1"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                disabled={unlimitedUses}
                placeholder="50"
              />
            </div>
          </div>
          <Checkbox
            label="Ilimitado"
            checked={unlimitedUses}
            onChange={(e) => setUnlimitedUses(e.target.checked)}
          />
        </div>

        {/* Expiry Date */}
        <div className="space-y-2">
          <Input
            label="Data limite"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            disabled={noExpiry}
            min={new Date().toISOString().split('T')[0]}
          />
          <Checkbox
            label="Sem data limite"
            checked={noExpiry}
            onChange={(e) => setNoExpiry(e.target.checked)}
          />
        </div>

        {/* Error */}
        {error && !error.includes('codigo') && (
          <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="ghost" type="button" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            isLoading={createMutation.isPending}
          >
            Criar Codigo
          </Button>
        </div>
      </form>
    </Modal>
  );
}
